import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertCircle, Boxes, Package, ClipboardList } from "lucide-react";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 3 }) : v);

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading || !data) return <div className="text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      <div>
        <div className="data-label">Overview</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <StatCard testid="stat-items" label="Active items" value={data.total_items} Icon={Package} />
        <StatCard testid="stat-containers" label="Containers in stock" value={data.total_containers} Icon={Boxes} />
        <StatCard testid="stat-approvals" label="Pending approvals" value={data.pending_approvals} Icon={ClipboardList}
                  accent={data.pending_approvals > 0} />
        <StatCard testid="stat-low" label="Low-stock items" value={data.low_stock?.length || 0} Icon={AlertCircle}
                  danger={data.low_stock?.some(s=>s.severity==="critical")} />
      </div>

      {/* Low stock */}
      <div className="bg-white border border-border rounded-sm">
        <div className="px-4 sm:px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Low stock</h2>
          <Link to="/stock" className="text-xs font-semibold uppercase tracking-widest text-primary">
            View stock →
          </Link>
        </div>
        {data.low_stock.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">Nothing low. All levels healthy.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="low-stock-list">
            {data.low_stock.map((s) => (
              <li key={s.item_id} className="p-4 sm:px-5 flex items-center gap-4 row-hover">
                <span className={`badge-flat ${s.severity === "critical"
                  ? "bg-destructive/10 text-destructive" : "bg-secondary/15 text-secondary"}`}>
                  {s.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{s.sku}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold">
                    {fmt(s.total_qty)} <span className="text-muted-foreground">{s.base_unit}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    of {fmt(s.reorder_level)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent transactions */}
      <div className="bg-white border border-border rounded-sm">
        <div className="px-4 sm:px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Recent activity</h2>
          <Link to="/activity" className="text-xs font-semibold uppercase tracking-widest text-primary">
            Full log →
          </Link>
        </div>
        <ul className="divide-y divide-border">
          {data.recent_transactions.map((r) => (
            <li key={r.id} className="p-3 sm:px-5 flex items-center gap-3 text-sm">
              <span className={`badge-flat ${typeBadge(r.type)}`}>{r.type}</span>
              <div className="flex-1 min-w-0 truncate">
                <span className="font-medium">{r.item_name}</span>{" "}
                <span className="text-muted-foreground">by {r.user_name}</span>
              </div>
              <div className="font-mono text-xs">
                {r.qty_change > 0 ? "+" : ""}{fmt(r.qty_change)}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value, Icon, testid, accent, danger }) {
  return (
    <div
      data-testid={testid}
      className={`stat-card flex flex-col justify-between min-h-[110px] ${
        danger ? "border-destructive/40" : accent ? "border-secondary/60" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="data-label">{label}</div>
        <Icon className={`w-4 h-4 ${danger ? "text-destructive" : accent ? "text-secondary" : "text-muted-foreground"}`} />
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value ?? 0}</div>
    </div>
  );
}

function typeBadge(t) {
  return {
    receipt: "bg-success/10 text-success",
    issue: "bg-destructive/10 text-destructive",
    move: "bg-muted text-foreground",
    split: "bg-secondary/15 text-secondary",
    merge: "bg-secondary/15 text-secondary",
    adjust: "bg-warning/15 text-yellow-800",
    revert: "bg-muted text-foreground",
  }[t] || "bg-muted text-foreground";
}
